import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Minus, ArrowRight } from 'lucide-react';
import { MediaUnitLink } from '@ooh/ui/components/media-unit-link';
import type { OfferVersionDiffRow } from '@ooh/contracts';

// ─── Field label lookup ────────────────────────────────────────
// The API identifiers OfferVersionDiffRowSchema allows for 'changed' rows
// (clientPriceNet | discountPercent | dateFrom | dateTo); falls back to the
// raw identifier for anything else so an unexpected field never disappears.
// A switch (rather than a dynamic-key t() call) keeps every translation key
// statically known to the typed i18next resources.

function useFieldLabel(field: string | undefined): string {
  const { t } = useTranslation('offers');
  switch (field) {
    case 'clientPriceNet':
      return t('workspace.versions.diff.fieldLabel.clientPriceNet');
    case 'discountPercent':
      return t('workspace.versions.diff.fieldLabel.discountPercent');
    case 'dateFrom':
      return t('workspace.versions.diff.fieldLabel.dateFrom');
    case 'dateTo':
      return t('workspace.versions.diff.fieldLabel.dateTo');
    default:
      return field ?? '';
  }
}

// ─── Unit identification ────────────────────────────────────────

/**
 * Builds a readable unit label from a diff row's identification fields (CU-86cb4wky5):
 * supplier identity, RW-Group display number, then city/address — each segment omitted
 * cleanly when null (legacy snapshot, or supplier identity redacted for this caller).
 * Falls back to the raw unitId as the primary label when every segment is null; the raw
 * unitId is otherwise returned as a secondary, copyable detail.
 */
function formatDiffUnitLabel(row: OfferVersionDiffRow): {
  primary: string;
  secondary: string | null;
} {
  const supplierSegment = [row.supplierNumber, row.supplierName].filter(Boolean).join(' – ');
  const locationSegment = [row.city, row.address].filter(Boolean).join(', ');
  const segments = [supplierSegment, row.mediaUnitNumber, locationSegment].filter(
    (segment): segment is string => Boolean(segment),
  );

  if (segments.length === 0) {
    return { primary: row.unitId, secondary: null };
  }
  return { primary: segments.join(' • '), secondary: row.unitId };
}

// ─── Diff section ─────────────────────────────────────────────

const SECTION_TONE_CLASSES = {
  emerald: {
    badge: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
    border: 'border-emerald-200 dark:border-emerald-800',
  },
  red: {
    badge: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    border: 'border-red-200 dark:border-red-800',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
    border: 'border-amber-200 dark:border-amber-800',
  },
} as const;

export function DiffSection({
  title,
  tone,
  icon,
  children,
}: {
  title: string;
  tone: keyof typeof SECTION_TONE_CLASSES;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  const cls = SECTION_TONE_CLASSES[tone];

  return (
    <section>
      <div className="mb-2 flex items-center gap-2">
        <span className={`inline-flex h-5 w-5 items-center justify-center rounded-md ${cls.badge}`}>
          {icon}
        </span>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-foreground">{title}</h3>
      </div>
      <div className={`overflow-hidden rounded-lg border ${cls.border}`}>{children}</div>
    </section>
  );
}

// ─── Diff summary strip ───────────────────────────────────────
// Takes prior/current net prices as plain nullable numbers (not the full
// OfferVersion) so this stays reusable from a caller that only has a mock
// version shape, not the real @ooh/contracts OfferVersion.

export function DiffSummary({
  addedCount,
  removedCount,
  changedCount,
  priorNetPrice,
  currentNetPrice,
}: {
  addedCount: number;
  removedCount: number;
  changedCount: number;
  priorNetPrice: number | null;
  currentNetPrice: number | null;
}) {
  const { t } = useTranslation('offers');
  const hasNetPrices = currentNetPrice != null && priorNetPrice != null;

  return (
    <div className="flex items-stretch rounded-lg border border-border/50 bg-muted/20">
      <div className="flex-1 px-4 py-3" data-testid="diff-summary-added">
        <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-400">
          <Plus className="h-3.5 w-3.5" />
          {t('workspace.versions.diff.summary.addedLabel')}
        </div>
        <div className="mt-1 text-lg font-bold text-foreground">{addedCount}</div>
      </div>
      <div
        className="flex-1 border-l border-border/60 px-4 py-3"
        data-testid="diff-summary-removed"
      >
        <div className="flex items-center gap-1.5 text-xs font-medium text-red-700 dark:text-red-400">
          <Minus className="h-3.5 w-3.5" />
          {t('workspace.versions.diff.summary.removedLabel')}
        </div>
        <div className="mt-1 text-lg font-bold text-foreground">{removedCount}</div>
      </div>
      <div
        className="flex-1 border-l border-border/60 px-4 py-3"
        data-testid="diff-summary-changed"
      >
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 dark:text-amber-400">
          <ArrowRight className="h-3.5 w-3.5" />
          {t('workspace.versions.diff.summary.changedLabel')}
        </div>
        <div className="mt-1 text-lg font-bold text-foreground">{changedCount}</div>
      </div>
      {hasNetPrices ? (
        <div
          className="flex-1 border-l border-border/60 px-4 py-3"
          data-testid="diff-summary-net-value"
        >
          <div className="text-xs font-medium text-muted-foreground">
            {t('workspace.versions.diff.summary.netValueLabel')}
          </div>
          <div className="mt-1 text-sm font-semibold">
            <span className="text-muted-foreground line-through">{`${priorNetPrice.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} zł`}</span>
            <span className="mx-1 text-muted-foreground">→</span>
            <span className="text-emerald-700 dark:text-emerald-400">{`${currentNetPrice.toLocaleString('pl-PL', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} zł`}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─── Diff row groups ──────────────────────────────────────────

export function AddedRemovedGroup({
  rows,
  lineThrough,
  tone,
  onNavigateToUnit,
}: {
  rows: OfferVersionDiffRow[];
  lineThrough: boolean;
  tone: 'emerald' | 'red';
  onNavigateToUnit?: (unitId: string) => void;
}) {
  const bg =
    tone === 'emerald'
      ? 'bg-emerald-50/60 dark:bg-emerald-900/20'
      : 'bg-red-50/60 dark:bg-red-900/20';
  const border =
    tone === 'emerald'
      ? 'border-emerald-200 dark:border-emerald-800'
      : 'border-red-200 dark:border-red-800';
  const testPrefix = tone === 'emerald' ? 'diff-row-added' : 'diff-row-removed';

  return (
    <>
      {rows.map((row, index) => {
        const { primary, secondary } = formatDiffUnitLabel(row);
        return (
          <div
            key={row.unitId}
            className={`${bg} px-4 py-3 ${index > 0 ? `border-t ${border}` : ''}`}
            data-testid={`${testPrefix}-${row.unitId}`}
          >
            <MediaUnitLink
              identifier={primary}
              subtitle={secondary ?? undefined}
              lineThrough={lineThrough}
              onActivate={onNavigateToUnit ? () => onNavigateToUnit(row.unitId) : undefined}
            />
          </div>
        );
      })}
    </>
  );
}

function ChangedRow({
  row,
  showTopBorder,
  onNavigateToUnit,
}: {
  row: OfferVersionDiffRow;
  showTopBorder: boolean;
  onNavigateToUnit?: (unitId: string) => void;
}) {
  const { primary, secondary } = formatDiffUnitLabel(row);
  const fieldLabel = useFieldLabel(row.field);
  // Disambiguate by field/changeType, not just unitId — a single unit can
  // produce multiple 'changed' rows in the same diff (e.g. dateFrom, dateTo,
  // clientPriceNet, discountPercent all changing at once).
  const rowKey = `${row.unitId}-${row.field ?? row.changeType}`;

  return (
    <div
      className={`bg-amber-50/40 px-4 py-3 dark:bg-amber-900/20 ${showTopBorder ? 'border-t border-amber-200 dark:border-amber-800' : ''}`}
      data-testid={`diff-row-changed-${rowKey}`}
    >
      <MediaUnitLink
        identifier={primary}
        subtitle={secondary ?? undefined}
        onActivate={onNavigateToUnit ? () => onNavigateToUnit(row.unitId) : undefined}
      />
      <div className="mt-1.5 flex items-center gap-2 text-xs">
        <span className="rounded bg-muted px-1.5 py-0.5 text-muted-foreground">{fieldLabel}</span>
        <span
          className="text-red-600 line-through dark:text-red-400"
          data-testid={`diff-row-changed-${rowKey}-prev`}
        >
          {row.previousValue ?? '-'}
        </span>
        <ArrowRight className="h-3 w-3 text-muted-foreground" />
        <span
          className="font-medium text-emerald-700 dark:text-emerald-400"
          data-testid={`diff-row-changed-${rowKey}-new`}
        >
          {row.newValue ?? '-'}
        </span>
      </div>
    </div>
  );
}

export function ChangedGroup({
  rows,
  onNavigateToUnit,
}: {
  rows: OfferVersionDiffRow[];
  onNavigateToUnit?: (unitId: string) => void;
}) {
  return (
    <>
      {rows.map((row, index) => (
        <ChangedRow
          key={`${row.unitId}-${row.field ?? row.changeType}`}
          row={row}
          showTopBorder={index > 0}
          onNavigateToUnit={onNavigateToUnit}
        />
      ))}
    </>
  );
}
